import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ImageBackupFileItem } from "../../view/contents/image/image.drive";
import { TableContainer } from "../../style/contents/image/image";
import Pagination from "../common/table.pagination";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchRequestDownloadDiskBackup } from "../../api/admin";

interface ImageDriveListTableProps {
  data: ImageBackupFileItem[];
  searchKeyword?: string;
  pageSize: number;
  isLoading?: boolean;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const formatFileSize = (size: number) => {
  if (!size) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value: string | number) => {
  if (!value) return "-";

  const timestamp =
    typeof value === "number" || /^\d+$/.test(String(value))
      ? Number(value)
      : value;
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return String(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toSearchText = (file: ImageBackupFileItem) =>
  [
    file.fileName,
    file.type,
    file.size,
    file.rootDirectoryPath,
    formatFileSize(Number(file.size)),
    formatDate(file.createdDate),
  ]
    .map((value) => String(value ?? "").toLowerCase())
    .join(" ");

const getRootDirectoryPath = (file: ImageBackupFileItem) => {
  const rootPath = file.rootDirectoryPath || file.fileName;

  if (!rootPath) return "";
  if (/^[a-zA-Z]:$/.test(rootPath)) return `${rootPath}/`;

  return rootPath;
};

const ImageDriveListTable = ({
  data,
  searchKeyword = "",
  pageSize,
  isLoading,
}: ImageDriveListTableProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const queryString = location.search;

  const handleDownloadClick = useCallback(async (file: ImageBackupFileItem) => {
    const adminUserId = localStorage.getItem("userId");

    if (!adminUserId) {
      alert("관리자 정보가 없습니다.");
      return;
    }

    const params = {
      targetUserId: file.userId,
      targetDeviceId: file.deviceId,
      diskBackupStamp: file.backupStamp,
    };

    try {
      await fetchRequestDownloadDiskBackup(adminUserId, params);
      alert("다운로드 요청이 접수되었습니다.");
    } catch (err) {
      console.error("디스크 백업 다운로드 요청 실패:", err);
      alert("다운로드 요청 중 오류가 발생했습니다.");
    }
  }, []);

  const columns = useMemo<ColumnDef<ImageBackupFileItem>[]>(
    () => [
      {
        header: "종류",
        accessorKey: "type",
        cell: (info) => {
          const type = info.getValue();
          if (type === "disk") return "드라이브 이미지";
          if (type === "image") return "이미지";
          return type ? String(type) : "-";
        },
        size: 160,
      },
      {
        header: "이름",
        accessorKey: "fileName",
        cell: ({ row, getValue }) => {
          const fileName = getValue();
          const rootDirectoryPath = getRootDirectoryPath(row.original);
          const params = new URLSearchParams(queryString);

          params.set("targetUserId", String(row.original.userId));
          params.set("targetDeviceId", row.original.deviceId);
          params.set("diskBackupStamp", row.original.backupStamp);

          return (
            <span
              className="clickable-folder"
              onClick={() => {
                if (!rootDirectoryPath) {
                  alert("루트 경로 정보가 없습니다.");
                  return;
                }

                params.set("directoryPath", rootDirectoryPath);
                navigate(`/image/contents?${params.toString()}`);
              }}
            >
              {fileName ? String(fileName) : "-"}
            </span>
          );
        },
        size: 400,
      },
      {
        header: "크기",
        accessorKey: "size",
        cell: (info) => formatFileSize(Number(info.getValue())),
        size: 140,
      },
      {
        header: "백업날짜",
        accessorKey: "createdDate",
        cell: (info) => formatDate(info.getValue() as string | number),
        size: 220,
      },
      {
        header: "다운로드",
        id: "download",
        cell: ({ row }) => (
          <button
            type="button"
            className="download-btn"
            onClick={(event) => {
              event.stopPropagation();
              handleDownloadClick(row.original);
            }}
          >
            다운로드
          </button>
        ),
        size: 120,
      },
    ],
    [handleDownloadClick, navigate, queryString],
  );

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return data;

    return data.filter((file) => toSearchText(file).includes(keyword));
  }, [data, searchKeyword]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  const totalRowCount = table.getRowCount();

  useEffect(() => {
    table.setPageSize(pageSize);
  }, [table, pageSize, filteredData]);

  useEffect(() => {
    table.setPageIndex(0);
  }, [table, searchKeyword, filteredData]);

  return (
    <TableContainer>
      <div className="table-head-wrap">
        <div className="left">
          <span className="table-total-row">전체 {totalRowCount}</span>
        </div>
      </div>
      {isLoading ? (
        <div className="loading">데이터를 불러오는 중입니다...</div>
      ) : (
        <div className="fixed-height" style={{ maxHeight: "36rem" }}>
          <table>
            <thead>
              {table.getHeaderGroups().map((item) => (
                <tr key={item.id}>
                  {item.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="no-data-row">
                    검색 결과가 없습니다.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {table.getPageCount() > 0 && (
        <Pagination
          page={pagination.pageIndex}
          pages={table.getPageCount()}
          onPageChange={(page) => {
            table.setPageIndex(page);
          }}
        />
      )}
    </TableContainer>
  );
};

export default ImageDriveListTable;
